/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Overlay flota — misma familia visual que Cambiar sucursal / Inicio hoja.
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import {
  ActivityIndicator,
  Pressable,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Toast from 'react-native-toast-message';
import { CONTENT_MAX_WIDTH_FORM } from '../../shared/layout/breakpoints'
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal'
import { ModalScreenHeader } from '../../shared/modals/ModalScreenHeader';
import { spacing } from '../../shared/theme/spacing';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { ticketChrome } from '../../shared/ticket/ticketChrome.styles'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { TicketButton } from '../../shared/ticket/TicketButton';
import { SettingsFormEditModal } from './components/SettingsFormEditModal';
import { useManageBranchesScreen } from './useManageBranchesScreen';
import { CrecePaywallSheet } from '../../shell/billing/components/CrecePaywallSheet';
import { crecePaywallBranchCopy } from '../../../../../shared/plan-crece-copy';
import { trackCreceFunnel } from '../../shell/billing/trackCreceFunnel';
import { CRECE_FUNNEL_EVENTS } from '../../../../../shared/crece-funnel';
import { appToastConfig, toastTopOffset } from '../../shared/ui/appToast'
import { useScreenRefresh } from '../../shared/ui/hooks-screen/useScreenRefresh';
import { branchMissingMapLocation } from '../domain/branchLocationStatus';

function ManageBranchesContent({ vm }: { vm: ReturnType<typeof useManageBranchesScreen> }) {
  if (!vm.isOwner) {
    return (
      <View style={styles.deniedCard}>
        <Text style={styles.deniedTitle}>Sin permiso</Text>
        <Text style={styles.deniedText}>Solo el dueño puede agregar o desactivar sucursales.</Text>
      </View>
    );
  }

  if (vm.loading) {
    return (
      <View style={styles.loadingWrap}>
        <ActivityIndicator color={ticketColors.gold} />
        <Text style={styles.loadingLabel}>Cargando sucursales…</Text>
      </View>
    );
  }

  const metaLead =
    vm.billingOn && vm.branchUsage?.line
      ? vm.branchUsage.line
      : vm.fleetLead;

  return (
    <>
      <View style={styles.metaBlock}>
        <Text style={styles.metaKicker}>Flota</Text>
        <Text style={[styles.metaLead, vm.branchUsage?.overQuota && styles.metaLeadWarning]}>
          {metaLead}
        </Text>
      </View>

      <View style={styles.group}>
        <Text style={styles.groupLabel}>Activas</Text>
        <View style={styles.listCard}>
          {vm.activeBranches.length === 0 ? (
            <Text style={styles.emptyHint}>No hay sucursales activas.</Text>
          ) : (
            vm.activeBranches.map((branch, index) => {
              const isCurrent = branch.id === vm.currentBranchId;
              const missingMap = branchMissingMapLocation(branch);
              return (
                <View key={branch.id}>
                  <View style={styles.branchRow}>
                    <View style={styles.branchText}>
                      <Text style={styles.branchName}>{branch.name}</Text>
                      <Text style={styles.branchLocation}>
                        {missingMap ? 'Sin ubicación' : branch.location}
                      </Text>
                      {isCurrent ? (
                        <Text style={styles.pillHere}>Operando acá</Text>
                      ) : null}
                      {missingMap ? (
                        <Text style={styles.pillWarn}>Falta el mapa</Text>
                      ) : null}
                    </View>
                    <View style={styles.branchActions}>
                      <TicketButton
                        label="Editar"
                        variant="ghost"
                        style={[styles.branchActionBtn, styles.editSecondaryBtn]}
                        textStyle={styles.editSecondaryBtnText}
                        onPress={() => vm.openBranchIdentity(branch.id)}
                        accessibilityLabel={`Editar sucursal ${branch.name}`}
                      />
                      <Pressable
                        onPress={() =>
                          vm.setDeactivateTarget({
                            id: branch.id,
                            name: branch.name,
                            location: branch.location,
                          })
                        }
                        style={({ pressed }) => [styles.textAction, pressed && styles.pressed]}
                        accessibilityRole="button"
                        accessibilityLabel={`Desactivar ${branch.name}`}
                      >
                        <Text style={styles.textActionLabel}>Desactivar</Text>
                      </Pressable>
                    </View>
                  </View>
                  {index < vm.activeBranches.length - 1 ? <View style={styles.separator} /> : null}
                </View>
              );
            })
          )}
        </View>
      </View>

      <View style={styles.group}>
        <Text style={styles.groupLabel}>Inactivas</Text>
        <Text style={styles.groupHint}>Podés reactivarlas cuando las necesites.</Text>
        <View style={styles.listCard}>
          {vm.inactiveBranches.length === 0 ? (
            <Text style={styles.emptyHint}>Ninguna por ahora.</Text>
          ) : (
            vm.inactiveBranches.map((branch, index) => {
              const missingMap = branchMissingMapLocation(branch);
              return (
                <View key={branch.id}>
                  <View style={styles.branchRow}>
                    <View style={styles.branchText}>
                      <Text style={styles.branchName}>{branch.name}</Text>
                      <Text style={styles.branchLocation}>
                        {missingMap ? 'Sin ubicación' : branch.location}
                      </Text>
                      {missingMap ? (
                        <Text style={styles.pillWarn}>Falta el mapa</Text>
                      ) : null}
                    </View>
                    <View style={styles.branchActions}>
                      <TicketButton
                        label="Editar"
                        variant="ghost"
                        style={[styles.branchActionBtn, styles.editSecondaryBtn]}
                        textStyle={styles.editSecondaryBtnText}
                        onPress={() => vm.openBranchEdit(branch)}
                        accessibilityLabel={`Editar ${branch.name}`}
                      />
                      <TicketButton
                        label="Reactivar"
                        variant="ghost"
                        style={[styles.branchActionBtn, styles.reactivateBtn]}
                        textStyle={styles.reactivateBtnText}
                        onPress={() => vm.setReactivateTarget(branch)}
                        accessibilityLabel={`Reactivar ${branch.name}`}
                      />
                    </View>
                  </View>
                  {index < vm.inactiveBranches.length - 1 ? <View style={styles.separator} /> : null}
                </View>
              );
            })
          )}
        </View>
      </View>

      <SettingsFormEditModal
        visible={vm.editTarget != null}
        title="Editar sucursal"
        fields={[
          {
            key: 'name',
            label: 'Nombre',
            value: vm.editNameInput,
            placeholder: 'Ej. Sucursal Centro',
          },
        ]}
        saving={vm.editSaving}
        onClose={vm.closeBranchEdit}
        onFieldChange={(key, value) => {
          if (key === 'name') vm.setEditNameInput(value);
        }}
        onSave={() => void vm.confirmSaveBranchEdit()}
      />

      <OperationalConfirmModal
        visible={vm.reactivateTarget != null}
        title="Reactivar sucursal"
        entityName={vm.reactivateTarget?.name}
        message="Vuelve al selector. Cuenta como sucursal activa."
        confirmLabel="Reactivar"
        loading={vm.reactivating}
        onClose={() => {
          if (!vm.reactivating) vm.setReactivateTarget(null);
        }}
        onConfirm={vm.confirmReactivate}
      />

      <OperationalConfirmModal
        visible={vm.deactivateTarget != null}
        title="Desactivar sucursal"
        entityName={vm.deactivateTarget?.name}
        message="¿Querés desactivar esta sucursal? No se borran ventas ni datos. Si hay caja abierta, primero cerrá el turno."
        confirmLabel="Desactivar"
        confirmDelaySeconds={vm.branchDeactivateConfirmDelaySeconds}
        destructive
        iconName="business-outline"
        loading={vm.deactivating}
        onClose={() => {
          if (!vm.deactivating) vm.setDeactivateTarget(null);
        }}
        onConfirm={vm.confirmDeactivate}
      />
    </>
  );
}

function ManageBranchesFooter({ vm }: { vm: ReturnType<typeof useManageBranchesScreen> }) {
  if (!vm.isOwner || vm.loading) return null;

  return (
    <View style={styles.foot}>
      <TouchableOpacity
        style={[styles.softBtn, !vm.canAddBranch && styles.softBtnDisabled]}
        onPress={vm.openCreateBranch}
        accessibilityRole="button"
        accessibilityLabel="Agregar sucursal"
        disabled={!vm.canAddBranch}
      >
        <Text style={styles.softBtnText}>＋ Agregar sucursal</Text>
      </TouchableOpacity>
      {!vm.canAddBranch && vm.billingOn ? (
        <TouchableOpacity onPress={vm.openBilling} accessibilityRole="button" style={styles.linkBtn}>
          <Text style={styles.linkBtnText}>Activar plan Crece</Text>
        </TouchableOpacity>
      ) : null}
      <CrecePaywallSheet
        visible={vm.crecePaywallVisible}
        copy={crecePaywallBranchCopy}
        onActivate={() => {
          trackCreceFunnel(CRECE_FUNNEL_EVENTS.ctaClick, { source: 'branch_paywall' });
          vm.openActivateCrece();
        }}
        onDismiss={vm.dismissCrecePaywall}
      />
    </View>
  );
}

/** Modal root: sheet (mobile) / dialog (desktop) — mismo patrón que Cambiar sucursal. */
export function ManageBranchesOverlayScreen() {
  const navigation = useNavigation<any>();
  const { layoutWebShell } = useContentLayout('settingsWide');
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const vm = useManageBranchesScreen();
  const loadRef = useRef(vm.load);
  loadRef.current = vm.load;

  const { refreshing, onRefresh } = useScreenRefresh(async () => {
    await vm.load({ force: true });
    await vm.refreshEntitlements({ force: true });
  });

  useFocusEffect(
    useCallback(() => {
      void loadRef.current({ force: true });
    }, []),
  );

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  const dismiss = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    }
  };

  const scrollContent = (
    <ModalAwareScrollView
      style={styles.overlayScroll}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={[ticketColors.gold]}
          tintColor={ticketColors.gold}
        />
      }
    >
      <ManageBranchesContent vm={vm} />
    </ModalAwareScrollView>
  );

  const toastHost = (
    <View style={styles.toastHost} pointerEvents="box-none">
      <Toast config={appToastConfig} topOffset={toastTopOffset(insets.top)} />
    </View>
  );

  if (layoutWebShell) {
    const dialogMaxWidth = Math.min(CONTENT_MAX_WIDTH_FORM, windowWidth - 48);
    const dialogMaxHeight = windowHeight * 0.88;

    return (
      <View style={styles.overlayRoot}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={dismiss}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View
          style={[
            ticketChrome.dialogCard,
            styles.dialogCard,
            { maxWidth: dialogMaxWidth, maxHeight: dialogMaxHeight },
          ]}
        >
          <View style={styles.dialogHeader}>
            <ModalScreenHeader onClose={dismiss} title="Tus sucursales" />
          </View>
          {scrollContent}
          <ManageBranchesFooter vm={vm} />
        </View>
        {toastHost}
      </View>
    );
  }

  const sheetMaxHeight = windowHeight * 0.92;

  return (
    <View style={styles.sheetOverlayRoot}>
      <Pressable
        style={StyleSheet.absoluteFill}
        onPress={dismiss}
        accessibilityRole="button"
        accessibilityLabel="Cerrar"
      />
      <View
        style={[
          ticketChrome.sheetCard,
          styles.sheetCard,
          {
            maxHeight: sheetMaxHeight,
            paddingBottom: Math.max(insets.bottom, spacing.sm),
          },
        ]}
      >
        <View style={ticketChrome.grabber} />
        <View style={styles.sheetHeader}>
          <ModalScreenHeader onClose={dismiss} title="Tus sucursales" />
        </View>
        {scrollContent}
        <ManageBranchesFooter vm={vm} />
      </View>
      {toastHost}
    </View>
  );
}

export const ManageBranchesScreen = ManageBranchesOverlayScreen;

const styles = StyleSheet.create({
  overlayRoot: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  sheetOverlayRoot: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  dialogCard: {
    width: '100%',
    overflow: 'hidden',
    zIndex: 1,
  },
  sheetCard: {
    width: '100%',
    zIndex: 1,
  },
  toastHost: {
    ...StyleSheet.absoluteFill,
    zIndex: 20,
    elevation: 20,
  },
  dialogHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  sheetHeader: {
    paddingHorizontal: 16,
    paddingBottom: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  overlayScroll: {
    flexGrow: 0,
    flexShrink: 1,
    backgroundColor: ticketColors.paper,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 24,
    gap: 16,
  },
  foot: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
    gap: 6,
    backgroundColor: ticketColors.paper,
  },
  softBtn: {
    minHeight: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  softBtnDisabled: {
    opacity: 0.5,
  },
  softBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  linkBtn: {
    paddingVertical: 4,
    alignItems: 'center',
  },
  linkBtnText: {
    fontSize: 13,
    color: ticketColors.gold,
    fontWeight: '600',
    fontFamily: ticketFonts.sans,
  },
  loadingWrap: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xxl,
    gap: spacing.sm,
  },
  loadingLabel: {
    fontSize: 14,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  deniedCard: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    padding: spacing.lg,
    gap: spacing.sm,
  },
  deniedTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  deniedText: {
    fontSize: 14,
    color: ticketColors.ink2,
    lineHeight: 20,
    fontFamily: ticketFonts.sans,
  },
  metaBlock: {
    gap: 4,
  },
  metaKicker: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    fontFamily: ticketFonts.sans,
  },
  metaLead: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontWeight: '600',
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  metaLeadWarning: {
    color: ticketColors.gold,
  },
  group: {
    gap: 6,
  },
  groupLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    paddingLeft: 4,
    fontFamily: ticketFonts.sans,
  },
  groupHint: {
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 18,
    paddingLeft: 4,
    paddingBottom: 2,
    fontFamily: ticketFonts.sans,
  },
  listCard: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  emptyHint: {
    fontSize: 13,
    color: ticketColors.ink3,
    paddingVertical: spacing.sm,
    fontFamily: ticketFonts.sans,
  },
  branchRow: {
    gap: spacing.sm,
    paddingVertical: spacing.sm,
  },
  branchText: {
    minWidth: 0,
    gap: 2,
  },
  branchName: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  branchLocation: {
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 18,
    fontFamily: ticketFonts.sans,
  },
  pillHere: {
    marginTop: 4,
    alignSelf: 'flex-start',
    fontSize: 11,
    fontWeight: '700',
    color: ticketColors.brandInk,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 999,
    overflow: 'hidden',
    backgroundColor: ticketColors.brandSoft,
    borderWidth: 1,
    borderColor: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  pillWarn: {
    marginTop: 4,
    alignSelf: 'flex-start',
    fontSize: 11,
    fontWeight: '700',
    color: '#B45309',
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 999,
    overflow: 'hidden',
    backgroundColor: ticketColors.warnBg,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    fontFamily: ticketFonts.sans,
  },
  branchActions: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  branchActionBtn: {
    flex: 1,
  },
  editSecondaryBtn: {
    backgroundColor: ticketColors.paper,
    borderColor: ticketColors.ruleSoft,
  },
  editSecondaryBtnText: {
    color: ticketColors.ink,
  },
  reactivateBtn: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.brandSoft,
  },
  reactivateBtnText: {
    color: ticketColors.brandInk,
  },
  textAction: {
    flex: 1,
    minHeight: 40,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 8,
  },
  textActionLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  pressed: { opacity: 0.7 },
  separator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: ticketColors.ruleSoft,
  },
});

